import { HttpClient } from '@angular/common/http';
import { Component, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

type ReportScope = 'active' | 'archived' | 'all';

interface ReportsOverviewResponse {
  generated_at: string;
  range: {
    from: string;
    to: string;
    day_count: number;
    archived_filter: ReportScope;
  };
  totals: {
    orders: number;
    active_orders: number;
    archived_orders: number;
    revenue: number;
    average_order_value: number;
    currency_code: string | null;
    multi_currency: boolean;
    revenue_by_currency: Array<{
      currency_code: string;
      order_count: number;
      revenue: number;
      average_order_value: number;
    }>;
    shipped_rate: number;
    packaging_ready_rate: number;
    label_coverage_rate: number;
    invoice_coverage_rate: number;
    cancelled_invoice_count: number;
  };
  attention: {
    overdue_orders: number;
    no_deadline_orders: number;
    missing_label_orders: number;
    missing_invoice_orders: number;
  };
  workflow: Array<{
    status: string;
    count: number;
    share: number;
  }>;
  merchants: Array<{
    merchant_id: number;
    merchant_name: string;
    order_count: number;
    revenue: number;
    share: number;
  }>;
  countries: Array<{
    country: string;
    order_count: number;
    revenue: number;
    share: number;
  }>;
  timeline: Array<{
    date: string;
    order_count: number;
    revenue: number;
    shipped_count: number;
    packaged_count: number;
    archived_count: number;
  }>;
  top_products: Array<{
    sku: string;
    name: string;
    quantity: number;
    revenue: number;
  }>;
}

@Component({
  selector: 'app-dashboards',
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DashboardsComponent {

  fetching = false;
  loadError = '';
  archivedScope: ReportScope = 'all';
  filterFormGroup!: FormGroup;
  overview: ReportsOverviewResponse | null = null;
  trendMaxOrders = 1;
  trendMaxRevenue = 1;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.createFilterFormGroup();
    this.fetchOverview();
  }

  createFilterFormGroup() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

    this.filterFormGroup = new FormGroup({
      from: new FormControl(oneMonthAgo),
      to: new FormControl(new Date()),
    });
  }

  onFiltersChanged() {
    this.fetchOverview();
  }

  setArchivedScope(scope: ReportScope) {
    if (this.archivedScope === scope) {
      return;
    }
    this.archivedScope = scope;
    this.fetchOverview();
  }


  getScopeLabel(scope: ReportScope): string {
    if (scope === 'active') return 'Aktív rendelések';
    if (scope === 'archived') return 'Archivált rendelések';
    return 'Minden rendelés';
  }

  getWorkflowLabel(status: string): string {
    if (status === 'new') return 'Új';
    if (status === 'label_generated') return 'Címke generálva';
    if (status === 'fulfilled') return 'Teljesítve';
    if (status === 'archived') return 'Archív';
    return status || 'Ismeretlen';
  }

  getTrendHeight(value: number, max: number): number {
    if (max <= 0 || value <= 0) {
      return 0;
    }
    return Math.max(4, (value / max) * 100);
  }

  getHealthClass(value: number): string {
    if (value >= 85) return 'is-good';
    if (value >= 60) return 'is-mid';
    return 'is-low';
  }

  trackByTimelineDay(_: number, item: { date: string }) {
    return item.date;
  }

  private fetchOverview() {
    this.fetching = true;
    this.loadError = '';

    const query = this.getCurrentQueryFilter();
    this.http.get<ReportsOverviewResponse>(`orders/reports/overview?${query}`).subscribe({
      next: (response) => {
        this.overview = response;
        this.recalculateTrendScale(response);
        this.fetching = false;
      },
      error: (error) => {
        this.overview = null;
        this.trendMaxOrders = 1;
        this.trendMaxRevenue = 1;
        this.fetching = false;
        this.loadError = `A riport adatok betöltése sikertelen. ${error?.error?.message || error?.message || ''}`.trim();
      }
    });
  }

  private recalculateTrendScale(data: ReportsOverviewResponse) {
    const maxOrders = data.timeline.reduce((max, item) => Math.max(max, item.order_count), 0);
    const maxRevenue = data.timeline.reduce((max, item) => Math.max(max, item.revenue), 0);
    this.trendMaxOrders = maxOrders > 0 ? maxOrders : 1;
    this.trendMaxRevenue = maxRevenue > 0 ? maxRevenue : 1;
  }

  private getCurrentQueryFilter(): string {
    const params = new URLSearchParams();
    const from = this.filterFormGroup.get('from')?.value;
    const to = this.filterFormGroup.get('to')?.value;
    const fromIso = this.toApiDate(from);
    const toIso = this.toApiDate(to);

    if (fromIso) {
      params.set('from', fromIso);
    }
    if (toIso) {
      params.set('to', toIso);
    }

    params.set('archived', this.archivedScope);
    return params.toString();
  }

  private toApiDate(value: unknown): string | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}
